/**
 * Company: SPHMedia
 * Description: [Hook for Brightcove media integration]
 */
import mediaController from "@helper/mediaController";
import type { BrightcovePlayer } from "@typings/Brightcove.d";
import { RefObject, useCallback, useEffect, useId, useRef } from "react";

let globalMutedState = true;

type useBrightcoveMediaData = {
  playerRef: RefObject<BrightcovePlayer | null>;
  handleSuccess: (success: { ref: BrightcovePlayer }) => void;
};

type useBrightcoveMediaProps = {
  onEnded?: () => void;
  nextPlaylist?: () => void;
  disableFullscreen?: boolean;
  pageRefValue?: string;
  onPlayerReady?: (player: BrightcovePlayer) => void;
  syncMuteAcrossPlayers?: boolean;
};

/**
 * Custom React Hook: useBrightcoveMediaHook
 * Description: [Brightcove media integration hook]
 * @param props - The props required by the hook (if any)
 * @returns data - The data returned by the hook
 */
export default function useBrightcoveMedia({
  onEnded,
  nextPlaylist,
  disableFullscreen,
  pageRefValue = "home",
  onPlayerReady,
  syncMuteAcrossPlayers = true,
}: useBrightcoveMediaProps): useBrightcoveMediaData {
  const playerRef = useRef<BrightcovePlayer | null>(null);
  const useRefId = useId();
  const playerRefId = useRef(`brightcove-ref-${useRefId.replace(/:/g, "")}`);

  // Fullscreen override for Brightcove player to open in a new tab instead of fullscreen
  const setupFullscreenOverride = useCallback(
    (player: BrightcovePlayer) => {
      const handleFullscreenChange = () => {
        if (player.isFullscreen?.()) {
          player.exitFullscreen?.();
        }
      };

      player.on("fullscreenchange", handleFullscreenChange);

      const fullscreenButton = player
        .el()
        .querySelector(".vjs-fullscreen-control");

      let justHandled = false;

      const openVideoTab = () => {
        player.pause(); // Pause the player before opening a new tab

        const videoId = player.mediainfo?.id;
        if (!videoId) return;

        const qs = new URLSearchParams({ ref: pageRefValue }).toString();
        window.open(
          `/quick-news/video/${videoId}?${qs}`,
          "_blank",
          "noopener,noreferrer",
        );
      };

      const handleFullScreenBehavior = (e: Event) => {
        // Always block the native fullscreen toggle, no matter which
        // event (pointerup vs click) triggered this.
        e.preventDefault();
        e.stopImmediatePropagation();

        // Dedupe: pointerup + click both fire for the same tap on iOS/most
        // touch browsers. Only run the side effect once per interaction.
        if (justHandled) return;
        justHandled = true;
        // Reset on next tick — long enough to swallow the paired event,
        // short enough not to affect the next real interaction.
        setTimeout(() => {
          justHandled = false;
        }, 350);

        openVideoTab();
      };

      // pointerup: needed because on iOS Safari the native fullscreen
      // request can be triggered before/without a click event firing.
      fullscreenButton?.addEventListener(
        "pointerup",
        handleFullScreenBehavior,
        true,
      );
      // click (capture): the event Video.js itself listens for, and
      // ensures we intercept before Video.js's own bubble handler.
      fullscreenButton?.addEventListener(
        "click",
        handleFullScreenBehavior,
        true,
      );

      return () => {
        player.off?.("fullscreenchange", handleFullscreenChange);

        fullscreenButton?.removeEventListener(
          "pointerup",
          handleFullScreenBehavior,
          true,
        );
        fullscreenButton?.removeEventListener(
          "click",
          handleFullScreenBehavior,
          true,
        );
      };
    },
    [pageRefValue],
  );

  /**
   * @param {Object} success - The success object containing reference to the player.
   * @param {ReactPlayer} success.ref - Reference to the player.
   */
  const handleSuccess = useCallback(
    (success: { ref: BrightcovePlayer | null }) => {
      const player = success.ref;

      if (!player) return;

      // Apply global mute state as early as possible
      if (syncMuteAcrossPlayers) {
        player.muted(globalMutedState);
      }

      const { onPlay, onPause } = mediaController.register({
        type: "brightcove",
        pause: () => {
          player.pause();
        },
        id: playerRefId.current,
      });

      // Call mediaController's onPlay when the player starts playing and is unmuted.
      // This ensures that other players are paused when this player starts playing.
      const triggerOnPlay = () => {
        const isMuted = player.muted();
        const isPlaying = !player.paused?.();

        if (!isMuted && isPlaying) {
          onPlay();
        }
      };

      player.on("loadedmetadata", () => {
        if (syncMuteAcrossPlayers) {
          player.muted(globalMutedState);
        }

        triggerOnPlay();
      });

      player.on("volumechange", () => {
        globalMutedState = player.muted();

        triggerOnPlay();
      });

      player.on("play", () => {
        // Re-apply global mute state on play to prevent sudden sound bursts
        if (syncMuteAcrossPlayers) {
          player.muted(globalMutedState);
        }

        triggerOnPlay();
      });

      player.on("playing", triggerOnPlay);

      player.on("pause", onPause);

      // Disable fullscreen handling here
      if (disableFullscreen) {
        const cleanupFullscreen = setupFullscreenOverride(player);
        player.on("dispose", cleanupFullscreen);
      }

      onPlayerReady?.(player);

      playerRef.current = player;

      if (onEnded) {
        player.on("ended", onEnded);
      }

      const nextButton = player.el().querySelector(".vjs-next-button");
      const handleNextPlaylist = () => nextPlaylist?.();

      nextButton?.addEventListener("click", handleNextPlaylist);
      player.on("dispose", () => {
        nextButton?.removeEventListener("click", handleNextPlaylist);
        onPause();
      });
    },
    [
      onEnded,
      nextPlaylist,
      disableFullscreen,
      setupFullscreenOverride,
      onPlayerReady,
      syncMuteAcrossPlayers,
    ],
  );

  useEffect(() => {
    function handlePauseAllPlayers(e: CustomEvent<{ sourceId: string }>) {
      const player = playerRef.current;
      if (!player) return;

      // Ignore if this Brightcove is the source
      if (e.detail.sourceId === playerRefId.current) return;

      try {
        player.pause();
      } catch (err) {
        console.warn("[mediaController] Failed to pause Brightcove:", err);
      }
    }

    window.addEventListener(
      "pauseAllPlayers",
      handlePauseAllPlayers as EventListener,
    );
    return () => {
      window.removeEventListener(
        "pauseAllPlayers",
        handlePauseAllPlayers as EventListener,
      );
    };
  }, []);

  return {
    playerRef,
    handleSuccess,
  };
}
